import { EXPORT_COLORS } from '@consigli/types';
import { saveBlob } from '@consigli/utils';
import ExcelJS from 'exceljs';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { toUTC } from '../format-meeting-datetime';
import {
  toTitleCase,
  getExportDateSuffix,
  stripDateFromTitle,
  sanitizeFilename,
} from '../utils/export-types';
import { fetchLogoAsArrayBuffer } from '../utils/logo-utils';
import { formatResponsibleParties } from '../utils/participant-utils';
import {
  cleanSummaryText,
  isWideChar,
  stripSpeakerPrefix,
  extractDiscussionStructure,
  extractSummaryIntro,
} from '../utils/text-utils';

import type { ExportData } from '../utils/export-types';

// Header style for table headers
const headerFill: ExcelJS.FillPattern = {
  type: 'pattern',
  pattern: 'solid',
  fgColor: { argb: EXPORT_COLORS.headerGrey.argb },
};

const headerFont: Partial<ExcelJS.Font> = {
  bold: true,
  color: { argb: EXPORT_COLORS.black.argb },
  size: 12,
};

const headerAlignment: Partial<ExcelJS.Alignment> = {
  vertical: 'middle',
  horizontal: 'left',
  wrapText: true,
};

const cellBorder: Partial<ExcelJS.Borders> = {
  top: { style: 'thin', color: { argb: 'FFB0B0B0' } },
  left: { style: 'thin', color: { argb: 'FFB0B0B0' } },
  bottom: { style: 'thin', color: { argb: 'FFB0B0B0' } },
  right: { style: 'thin', color: { argb: 'FFB0B0B0' } },
};

// Data cell style
const dataFont: Partial<ExcelJS.Font> = {
  size: 12,
};

const dataAlignment: Partial<ExcelJS.Alignment> = {
  vertical: 'top',
  horizontal: 'left',
  wrapText: true,
  indent: 1,
};

// Centered alignment for ID columns
const centeredAlignment: Partial<ExcelJS.Alignment> = {
  vertical: 'middle',
  horizontal: 'center',
  wrapText: false,
};

// Section label style
const sectionLabelFont: Partial<ExcelJS.Font> = {
  bold: true,
  size: 12,
  color: { argb: EXPORT_COLORS.black.argb }, // Black for section headers
};

const sectionLabelAlignment: Partial<ExcelJS.Alignment> = {
  vertical: 'middle',
  horizontal: 'left',
};

// Title style for meeting title
const titleFont: Partial<ExcelJS.Font> = {
  bold: true,
  size: 14,
  color: { argb: EXPORT_COLORS.black.argb },
};

const titleAlignment: Partial<ExcelJS.Alignment> = {
  vertical: 'middle',
  horizontal: 'right',
};

// Helper to add logo to worksheet - positioned at top left
const addLogoToSheet = (
  workbook: ExcelJS.Workbook,
  worksheet: ExcelJS.Worksheet,
  logoBuffer: ArrayBuffer,
  meetingTitle: string,
) => {
  // Add the logo image to the workbook
  const imageId = workbook.addImage({
    buffer: logoBuffer,
    extension: 'png',
  });

  // Add image to the worksheet - positioned at top left (column A)
  worksheet.addImage(imageId, {
    tl: { col: 0, row: 0.3 },
    ext: { width: 180, height: 45 },
  });

  // Add meeting title on right side (row 2)
  const titleCell = worksheet.getCell('J2');
  titleCell.value = toTitleCase(meetingTitle);
  titleCell.font = titleFont;
  titleCell.alignment = titleAlignment;

  // Leave some space after the header area
  worksheet.getRow(1).height = 30;
  worksheet.getRow(2).height = 30;
  worksheet.getRow(3).height = 10; // spacer row

  // Set page setup for landscape and fit to page width
  worksheet.pageSetup = {
    orientation: 'landscape',
    fitToPage: true,
    fitToWidth: 1,
    fitToHeight: 0, // 0 means as many pages as needed for height
    paperSize: 9, // A4
    margins: {
      left: 0.25,
      right: 0.25,
      top: 0.5,
      bottom: 0.5,
      header: 0.3,
      footer: 0.3,
    },
  };
};

// Helper to add a section label above the table
const addSectionLabel = (worksheet: ExcelJS.Worksheet, row: number, label: string) => {
  const cell = worksheet.getCell(row, 1); // (row, column) - column 1 = A
  cell.value = label;
  cell.font = sectionLabelFont;
  cell.alignment = sectionLabelAlignment;
  worksheet.getRow(row).height = 20;
};

/**
 * Estimates the rendered row height (in points) for wrapped cell content.
 *
 * Excel column width ≈ number of default-font characters that fit per line, so
 * full-width CJK characters are weighted 2x to avoid under-counting lines.
 * Explicit newlines are treated as hard breaks. A small buffer and minimum keep
 * single-line rows comfortable; a cap prevents runaway heights on huge cells.
 */
const calculateRowHeight = (
  rowData: (string | number)[],
  columnWidths: number[],
  fontSize: number = 12,
): number => {
  const lineHeight = fontSize * 1.6; // Line height in points (with leading)
  const minHeight = 22;
  const maxHeight = 409; // Excel's per-row maximum

  let maxLines = 1;
  rowData.forEach((value, index) => {
    const text = String(value);
    if (!text) return;

    const colWidth = columnWidths[index] || 20;
    // Reserve ~2 units for cell padding so text isn't flush to the border.
    const charsPerLine = Math.max(1, colWidth - 2);

    // Compute wrapped lines per hard-break segment, then sum. Full-width CJK
    // characters are weighted 2x so wide scripts get enough vertical space.
    let totalLines = 0;
    for (const segment of text.split('\n')) {
      let effectiveLength = 0;
      for (const char of segment) {
        effectiveLength += isWideChar(char) ? 2 : 1;
      }
      totalLines += Math.max(1, Math.ceil(effectiveLength / charsPerLine));
    }

    maxLines = Math.max(maxLines, totalLines);
  });

  // Add a little vertical breathing room on top of the wrapped lines.
  return Math.min(maxHeight, Math.max(minHeight, maxLines * lineHeight + 4));
};

// Helper to style a table
const styleTable = (
  worksheet: ExcelJS.Worksheet,
  startRow: number,
  headers: string[],
  dataRows: (string | number)[][],
  columnWidths: number[],
  centeredColumns: number[] = [], // Array of column indices (0-based) to center
) => {
  // Add headers
  const headerRow = worksheet.getRow(startRow);
  headers.forEach((header, index) => {
    const cell = headerRow.getCell(index + 1);
    cell.value = header;
    cell.fill = headerFill;
    cell.font = headerFont;
    cell.alignment = centeredColumns.includes(index)
      ? { ...headerAlignment, horizontal: 'center' }
      : headerAlignment;
    cell.border = cellBorder;
  });
  headerRow.height = 22;

  // Add data rows
  dataRows.forEach((rowData, rowIndex) => {
    const row = worksheet.getRow(startRow + 1 + rowIndex);
    rowData.forEach((value, colIndex) => {
      const cell = row.getCell(colIndex + 1);
      cell.value = value;
      cell.font = dataFont;
      cell.alignment = centeredColumns.includes(colIndex) ? centeredAlignment : dataAlignment;
      cell.border = cellBorder;
    });
    // Calculate and set row height based on content
    row.height = calculateRowHeight(rowData, columnWidths);
  });

  // Set column widths
  columnWidths.forEach((width, index) => {
    worksheet.getColumn(index + 1).width = width;
  });
};

export const useExportExcel = () => {
  const { t, i18n } = useTranslation();
  const exportToExcel = useCallback(
    async (data: ExportData) => {
      const { meeting, transcripts, summary, actionItems, agreements } = data;

      const workbook = new ExcelJS.Workbook();
      workbook.creator = 'Consigli Meeting Secretary';
      workbook.created = new Date();

      // Fetch logo
      const logoBuffer = await fetchLogoAsArrayBuffer();

      const startDate = new Date(toUTC(meeting.scheduledStartTime));
      const endDate = new Date(toUTC(meeting.scheduledEndTime));
      const durationMinutes = Math.round((endDate.getTime() - startDate.getTime()) / (1000 * 60));

      const dateStr = startDate.toLocaleDateString(i18n.language, {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });

      const timeStr = startDate.toLocaleTimeString(i18n.language, {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
      });

      // Clean the meeting title (strip date if present since it's shown separately)
      const cleanedTitle = stripDateFromTitle(meeting.title);

      // ===== Sheet 1: Summary =====
      const summarySheet = workbook.addWorksheet(t('meeting-secretary.exports.summary'));
      addLogoToSheet(workbook, summarySheet, logoBuffer, cleanedTitle);

      // Add section label at row 5 (above the table at row 6)
      addSectionLabel(summarySheet, 5, t('meeting-secretary.exports.summary'));

      // Summary table starts at row 6
      const summaryHeaders = [
        t('meeting-secretary.exports.headers.title'),
        t('meeting-secretary.exports.headers.text'),
      ];

      // Build the summary rows. When the summary is structured we split the
      // "Key Discussion Points" breakdown across multiple rows — one per topic —
      // so no single cell exceeds Excel's per-row height cap (~409pt) and gets
      // visually clipped. Unstructured summaries stay as a single prose cell.
      const discussionStructure = extractDiscussionStructure(summary?.generatedSummaryText || '');
      const summaryIntro =
        discussionStructure.length > 0
          ? extractSummaryIntro(summary?.generatedSummaryText)
          : cleanSummaryText(summary?.generatedSummaryText) || '';

      const summaryData: (string | number)[][] = [
        [t('meeting-secretary.exports.headers.title'), toTitleCase(cleanedTitle)],
        [t('meeting-secretary.exports.headers.date'), dateStr],
        [t('meeting-secretary.exports.headers.time'), `${timeStr} (${durationMinutes} min)`],
        [t('meeting-secretary.exports.headers.organizer'), meeting.organizerEmail ?? ''],
        [t('meeting-secretary.exports.summary'), summaryIntro],
      ];

      // 0-based index within summaryData where the discussion breakdown begins,
      // used below to emphasise the "Key Discussion Points" label and each topic
      // header cell.
      const discussionStartIndex = summaryData.length;

      if (discussionStructure.length > 0) {
        summaryData.push([t('meeting-secretary.exports.key-discussion-points'), '']);
        discussionStructure.forEach((section) => {
          const pointsText = section.points.map((point) => `• ${point}`).join('\n');
          summaryData.push([section.header, pointsText]);
        });
      }

      const summaryTableStartRow = 6;
      styleTable(summarySheet, summaryTableStartRow, summaryHeaders, summaryData, [28, 80]);

      // Emphasise the breakdown's header column so the Excel summary keeps the
      // same visual hierarchy as the PDF/DOCX exports (bold topic headings).
      if (discussionStructure.length > 0) {
        const firstDataRow = summaryTableStartRow + 1; // header row, then data
        for (let i = discussionStartIndex; i < summaryData.length; i++) {
          const headerCell = summarySheet.getRow(firstDataRow + i).getCell(1);
          headerCell.font = { ...dataFont, bold: true };
        }
      }

      // ===== Sheet 2: Action Items =====
      const actionItemsSheet = workbook.addWorksheet(t('meeting-secretary.exports.action-items'));
      addLogoToSheet(workbook, actionItemsSheet, logoBuffer, cleanedTitle);

      // Add section label at row 5 (above the table at row 6)
      addSectionLabel(actionItemsSheet, 5, t('meeting-secretary.exports.action-items'));

      const actionItemsHeaders = [
        t('meeting-secretary.exports.headers.id'),
        t('meeting-secretary.exports.headers.action'),
        t('meeting-secretary.exports.headers.responsible'),
        t('meeting-secretary.exports.headers.status'),
      ];
      const actionItemsData = actionItems.map((item, index) => [
        String(index + 1),
        item.descriptionText || '',
        item.responsiblePerson?.name || '',
        item.completionStatus || 'pending',
      ]);
      styleTable(actionItemsSheet, 6, actionItemsHeaders, actionItemsData, [8, 60, 28, 18], [0]); // Center ID column

      // ===== Sheet 3: Agreements =====
      const agreementsSheet = workbook.addWorksheet(t('meeting-secretary.exports.agreements'));
      addLogoToSheet(workbook, agreementsSheet, logoBuffer, cleanedTitle);

      // Add section label at row 5 (above the table at row 6)
      addSectionLabel(agreementsSheet, 5, t('meeting-secretary.exports.agreements'));

      const agreementsHeaders = [
        t('meeting-secretary.exports.headers.id'),
        t('meeting-secretary.exports.headers.agreement'),
        t('meeting-secretary.exports.headers.responsible'),
        t('meeting-secretary.exports.headers.details'),
      ];
      const agreementsData = agreements.map((agreement, index) => [
        String(index + 1),
        agreement.agreement || '',
        formatResponsibleParties(agreement.responsibleParties),
        stripSpeakerPrefix(
          agreement.sourceTextFromTranscript,
          formatResponsibleParties(agreement.responsibleParties),
        ),
      ]);
      styleTable(agreementsSheet, 6, agreementsHeaders, agreementsData, [8, 55, 30, 55], [0]); // Center ID column

      // ===== Sheet 4: Transcript =====
      const transcriptSheet = workbook.addWorksheet(t('meeting-secretary.exports.transcript'));
      addLogoToSheet(workbook, transcriptSheet, logoBuffer, cleanedTitle);

      // Add section label at row 5 (above the table at row 6)
      addSectionLabel(transcriptSheet, 5, t('meeting-secretary.exports.transcript'));

      const transcriptHeaders = [
        t('meeting-secretary.exports.headers.id'),
        t('meeting-secretary.exports.headers.timestamp'),
        t('meeting-secretary.exports.headers.speaker'),
        t('meeting-secretary.exports.headers.text'),
      ];
      const transcriptData = transcripts.map((entry, index) => [
        String(index + 1),
        entry.startTime,
        entry.speaker,
        entry.text,
      ]);
      styleTable(transcriptSheet, 6, transcriptHeaders, transcriptData, [8, 16, 24, 75], [0]); // Center ID column

      // Generate the file
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });

      // Sanitize filename so non-Latin titles export correctly
      const titleCaseFilename = toTitleCase(stripDateFromTitle(meeting.title));
      const sanitizedFilename = sanitizeFilename(titleCaseFilename);
      const dateSuffix = getExportDateSuffix(meeting.title, meeting.scheduledStartTime);
      const filename = `${sanitizedFilename}${dateSuffix}.xlsx`;
      saveBlob(filename, blob);
    },
    [t, i18n.language],
  );

  return { exportToExcel };
};
